import Tabs from "../../General/ResponsiveTabs/ResponsiveTabs";
import React, { useCallback, useState } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  Tab,
  TextField,
  Typography,
  Button,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Lead, LeadMovementHistory } from "./types/LeadBoardTypes";
import LeadColumn from "./LeadColumn/LeadColumn";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useMsal } from "@azure/msal-react";
import ProcessedLeadForm from "./ProcessedLead/ProcessedLeadForm";
import CommentsSection, { LeadComment } from "./CommentSection/CommentSection";
import ProcessedLeadView from "./ProcessedLead/ProcessedLeadView";
import AdditionalDetailsDialog from "./AdditionalDetailsDialog/AdditionalDetailsDialog";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import MovementHistory from "./MovementHistory/MovementHistory";
import dayjs from "dayjs";

const statuses: string[] = [
  "New Lead",
  "Contacted",
  "Not interested",
  "Interested",
  "Negotiating",
  "Closed",
];

export const fetchLeads = async (
  token: string | null | undefined,
  month?: number,
  year?: number,
): Promise<Lead[]> => {
  const url =
    month && year
      ? `${apiVariable}/leads/created?month=${month}&year=${year}`
      : `${apiVariable}/leads`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error("Error fetching leads");
  }
  return response.json();
};

const updateLeadStatusAPI = async (
  lead: Lead,
  token: string | null | undefined,
) => {
  const response = await fetch(`${apiVariable}/lead`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(lead),
  });
  if (!response.ok) {
    throw new Error("Error updating lead status");
  }
  return response.json();
};

const updateLeadAdditionalDetailsAPI = async (
  lead: Lead,
  token: string | null | undefined,
) => {
  const response = await fetch(`${apiVariable}/lead/additional-details`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(lead),
  });
  if (!response.ok) {
    throw new Error("Error updating lead additional details");
  }
  return response.json();
};
const fetchReminders = async (token: string | null | undefined) => {
  const response = await fetch(`${apiVariable}/reminder`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error("Error fetching reminders");
  }
  return response.json();
};

const LeadBoard: React.FC<{ month: number; year: number }> = ({
  month,
  year,
}) => {
  const mobile = useMediaQuery(useTheme().breakpoints.down("lg"));
  const [visibleStatus, setVisibleStatus] = useState(statuses[0]);
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const { accounts } = useMsal();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogFieldLabel, setDialogFieldLabel] = useState("");
  const [currentLead, setCurrentLead] = useState<Lead | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [processedLeadData, setProcessedLeadData] = useState<any>(null);
  const [statusSnackbar, setStatusSnackbar] = useState<
    "idle" | "error" | "success"
  >("idle");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [leadHistory, setLeadHistory] = useState<LeadMovementHistory[]>([]);
  const useComments = (
    processedLeadId: string | null,
    token: string | null | undefined,
  ) => {
    return useQuery({
      queryKey: ["comments", processedLeadId],
      queryFn: async () => {
        if (!processedLeadId) return [];
        const response = await fetch(
          `${apiVariable}/processed-lead/${processedLeadId}/comments`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) {
          throw new Error("Error fetching comments");
        }
        return response.json();
      },
      enabled: !!processedLeadId && !!token, // Only fetch if ID and token exist
    });
  };
  const { data: leadComments = [], isLoading: commentsLoading } = useComments(
    processedLeadData?.id || null,
    token,
  );
  const useEditComment = (
    token: string | null | undefined,
    processedLeadId: string | null,
  ) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (updatedComment: LeadComment) => {
        const response = await fetch(`${apiVariable}/lead/comment`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedComment),
        });
        if (!response.ok) {
          throw new Error("Error editing comment");
        }
        return response.json();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["comments", processedLeadId],
        });
      },
    });
  };

  const useDeleteComment = (
    token: string | null | undefined,
    processedLeadId: string | null,
  ) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (id: string) => {
        const response = await fetch(`${apiVariable}/lead/comment/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Error deleting comment");
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["comments", processedLeadId],
        });
      },
    });
  };

  const editCommentMutation = useEditComment(
    token,
    processedLeadData?.id || null,
  );
  const deleteCommentMutation = useDeleteComment(
    token,
    processedLeadData?.id || null,
  );

  const handleEditComment = (updatedComment: LeadComment) => {
    editCommentMutation.mutate(updatedComment);
  };

  const handleDeleteComment = (id: string) => {
    deleteCommentMutation.mutate(id);
  };
  const {
    data: leads = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: ["leads", month, year],
    queryFn: () => fetchLeads(token, month, year),
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
  });

  const { data: reminders = [], isLoading: remindersLoading } = useQuery({
    queryKey: ["reminders"],
    queryFn: () => fetchReminders(token),
  });

  const overdueLeads = reminders.reduce(
    (
      acc: { [x: string]: boolean },
      reminder: {
        isCompleted: any;
        scheduledTime: string | number | Date | dayjs.Dayjs | null | undefined;
        leadId: string | number;
      },
    ) => {
      if (
        !reminder.isCompleted &&
        dayjs(reminder.scheduledTime).isBefore(dayjs()) &&
        reminder.leadId
      ) {
        acc[reminder.leadId] = true;
      }
      return acc;
    },
    {},
  );

  const fetchProcessedLead = async (leadId: string) => {
    try {
      const response = await fetch(`${apiVariable}/processed-lead/${leadId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error("Error fetching processed lead");
      }
      return response.json();
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const fetchLeadHistory = async (leadId: string) => {
    try {
      const response = await fetch(`${apiVariable}/lead/${leadId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Error fetching lead history");
      }
      const data = await response.json();
      return data.history || [];
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const mutation = useMutation({
    mutationFn: (lead: Lead) => updateLeadStatusAPI(lead, token),
    onMutate: async (updatedLead: Lead) => {
      await queryClient.cancelQueries({ queryKey: ["leads"] });

      const previousLeads = queryClient.getQueryData<Lead[]>(["leads"]);

      if (previousLeads) {
        queryClient.setQueryData(["leads"], (oldLeads: Lead[] | undefined) =>
          oldLeads?.map((lead) =>
            lead.id === updatedLead.id
              ? { ...lead, status: updatedLead.status }
              : lead,
          ),
        );
      }

      return { previousLeads };
    },
    onError: (err, updatedLead, context) => {
      if (context?.previousLeads) {
        queryClient.setQueryData(["leads"], context.previousLeads);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });

  const updateAdditionalDetailsMutation = useMutation({
    mutationFn: (lead: Lead) => updateLeadAdditionalDetailsAPI(lead, token),
    onMutate: async (updatedLead: Lead) => {
      await queryClient.cancelQueries({ queryKey: ["leads"] });

      const previousLeads = queryClient.getQueryData<Lead[]>(["leads"]);

      if (previousLeads) {
        queryClient.setQueryData(["leads"], (oldLeads: Lead[] | undefined) =>
          oldLeads?.map((lead) =>
            lead.id === updatedLead.id ? { ...lead, ...updatedLead } : lead,
          ),
        );
      }

      return { previousLeads };
    },
    onError: (err, updatedLead, context) => {
      if (context?.previousLeads) {
        queryClient.setQueryData(["leads"], context.previousLeads);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });

  const updateLeadAdditionalDetails = (lead: Lead) => {
    updateAdditionalDetailsMutation.mutate(lead);
  };

  const updateLeadStatus = useCallback(
    (id: string, newStatus: string) => {
      const lead = leads.find((lead) => lead.id === id);
      if (lead) {
        if (newStatus === "Contacted" || newStatus === "Interested") {
          setCurrentLead({ ...lead, status: newStatus });
          setDialogOpen(true);
          setDialogFieldLabel(
            newStatus === "Contacted" ? "Contact Method" : "Interested In",
          );
        } else {
          mutation.mutate({ ...lead, status: newStatus });
        }
      }
    },
    [leads, mutation],
  );

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setDialogFieldLabel("");
    setCurrentLead(null);
  }, []);

  const handleDialogSubmit = useCallback(
    (value: string, contactedDate?: string, contactMethod?: string) => {
      if (currentLead) {
        const updatedLead = { ...currentLead };
        if (currentLead.status === "Contacted") {
          updatedLead.contactMethod = contactMethod;
          updatedLead.contactedDate = contactedDate;
        } else if (currentLead.status === "Interested") {
          updatedLead.interestedIn = value;
        }
        updateLeadAdditionalDetails(updatedLead);
      }
      handleDialogClose();
    },
    [currentLead, updateLeadAdditionalDetails, handleDialogClose],
  );

  const handleLeadDoubleClick = useCallback(
    (lead: Lead) => {
      setCurrentLead(lead);
      Promise.all([
        fetchProcessedLead(lead.id),
        fetchLeadHistory(lead.id),
      ]).then(([processedLead, history]) => {
        if (processedLead) {
          setProcessedLeadData(processedLead);
          setIsEditMode(false);
          queryClient.invalidateQueries({
            queryKey: ["comments", processedLead.id],
          });
        } else {
          setProcessedLeadData(null);
          setIsEditMode(true);
        }
        setLeadHistory(history);
        setDrawerOpen(true);
      });
    },
    [fetchProcessedLead, fetchLeadHistory, leadComments, setDrawerOpen],
  );

  const handleProcessedLeadSubmit = async (data: any) => {
    try {
      await saveProcessedLead(data);
      const freshProcessedLead = await fetchProcessedLead(
        currentLead?.id || "",
      );
      if (freshProcessedLead) {
        setProcessedLeadData(freshProcessedLead);
        setIsEditMode(false);
        queryClient.invalidateQueries({
          queryKey: ["comments", freshProcessedLead.id],
        });
      }
      const history = await fetchLeadHistory(currentLead?.id || "");
      setLeadHistory(history);
    } catch (error) {
      console.error("Error updating processed lead", error);
    }
  };

  const saveProcessedLead = async (data: any) => {
    try {
      const response = await fetch(`${apiVariable}/processed-lead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Error saving processed lead");
      }
      return response.json();
    } catch (error) {
      console.error(error);
    }
  };

  const handleFollowUp = async (months: number) => {
    if (!currentLead) return;
    try {
      await fetch(`${apiVariable}/reminder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          description: `Contact ${currentLead.company}${
            processedLeadData?.directPhone
              ? ` at ${processedLeadData.directPhone}`
              : ""
          }`,
          scheduledTime: dayjs().add(months, "month").format("YYYY-MM-DD"),
          isCompleted: false,
          leadId: currentLead.id,
        }),
      });
      setStatusSnackbar("success");
      setSnackbarMessage("Reminder created successfully");
      queryClient.invalidateQueries({ queryKey: ["reminders"] });
    } catch (error) {
      console.error("Error creating reminder", error);
      setStatusSnackbar("error");
      setSnackbarMessage("Failed to create reminder");
    }
  };

  const handleAddComment = useCallback(
    async (commentText: string) => {
      if (commentText.trim() === "" || !processedLeadData) {
        setStatusSnackbar("error");
        setSnackbarMessage("Save lead info first");
        return;
      }

      const commentData = {
        processedLeadId: processedLeadData.id,
        comment: commentText,
        commentedBy: accounts[0]?.name || "Anonymous",
      };

      try {
        const response = await fetch(
          `${apiVariable}/processed-lead/${processedLeadData.id}/comments`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(commentData),
          },
        );
        if (response.ok) {
          await response.json();
          queryClient.invalidateQueries({
            queryKey: ["comments", processedLeadData.id],
          });
          setStatusSnackbar("success");
          setSnackbarMessage("Comment added successfully");
        }
      } catch (error) {
        setStatusSnackbar("error");
        setSnackbarMessage(
          "Error adding comment, make sure you saved lead info first, and reopened the drawer",
        );
      }
    },
    [processedLeadData, accounts, token],
  );

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setDrawerTab(newValue);
    },
    [],
  );
  const filteredLeads = leads.filter((lead) =>
    [
      lead.company,
      lead.dot,
      lead.mc,
      lead.contactMethod,
      lead.interestedIn,
      lead.contactedDate ? dayjs(lead.contactedDate).format("MM/DD/YYYY") : "",
    ]
      .filter(Boolean)
      .some((field: any) =>
        field.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
  );

  return (
    <>
      <Box sx={{ padding: 2 }}>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Search Leads
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by company name, DOT, MC, contact method, interested in, or contacted date"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>
      {isLoading ? (
        <CircularProgress />
      ) : error ? (
        <div>Error loading leads: {error.message}</div>
      ) : (
        <Box>
          {mobile && (
            <TextField
              select
              fullWidth
              label="Lead status"
              value={visibleStatus}
              onChange={(event) => setVisibleStatus(event.target.value)}
              sx={{ mb: 2 }}
            >
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status} (
                  {
                    filteredLeads.filter((lead) => lead.status === status)
                      .length
                  }
                  )
                </MenuItem>
              ))}
            </TextField>
          )}
          <Box sx={{ display: "flex", maxWidth: "100%", overflowX: "auto" }}>
            {(mobile ? [visibleStatus] : statuses).map((status) => (
              <LeadColumn
                key={`${status}-${month}-${year}`}
                status={status}
                leads={filteredLeads.filter((lead) => lead.status === status)}
                onDrop={updateLeadStatus}
                onLeadDoubleClick={handleLeadDoubleClick}
                overdueLeads={overdueLeads}
              />
            ))}
          </Box>
        </Box>
      )}

      <AdditionalDetailsDialog
        open={dialogOpen}
        fieldLabel={dialogFieldLabel}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
      />
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setProcessedLeadData(null);
        }}
        sx={{ "& .MuiDrawer-paper": { top: 64, height: "calc(100% - 64px)" } }}
      >
        <Box sx={{ maxWidth: "100%", width: 400, padding: 2 }}>
          <Button onClick={() => setDrawerOpen(false)}>
            Close lead details
          </Button>
          {mobile && currentLead && (
            <TextField
              select
              fullWidth
              label="Status"
              value={currentLead.status}
              sx={{ my: 2 }}
              onChange={(event) => {
                setDrawerOpen(false);
                updateLeadStatus(currentLead.id, event.target.value);
              }}
            >
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Tabs
            value={drawerTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Lead Info" />
            <Tab label="Comments" />
          </Tabs>
          {drawerTab === 0 && (
            <Box sx={{ paddingTop: 2 }}>
              {isEditMode ? (
                <ProcessedLeadForm
                  lead={currentLead}
                  processedLead={processedLeadData}
                  onSubmit={handleProcessedLeadSubmit}
                />
              ) : (
                <ProcessedLeadView
                  processedLead={processedLeadData}
                  onEdit={() => setIsEditMode(true)}
                  onFollowUp={handleFollowUp}
                />
              )}
              <MovementHistory history={leadHistory} />
            </Box>
          )}
          {drawerTab === 1 && (
            <Box
              sx={{
                paddingTop: 2,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <CommentsSection
                comments={leadComments}
                handleAddComment={handleAddComment}
                handleEditComment={handleEditComment}
                handleDeleteComment={handleDeleteComment}
              />
            </Box>
          )}
          <StatusSnackbar
            status={statusSnackbar}
            successMessage={snackbarMessage}
            errorMessage={snackbarMessage}
            handleClose={() => {
              setStatusSnackbar("idle");
              setSnackbarMessage("");
            }}
          />
        </Box>
      </Drawer>
    </>
  );
};

export default LeadBoard;
